import { Link, useSearchParams } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { LoginForm } from '#app/components/login-form.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { loginAction } from '#app/routes/_auth/login.server.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { BRAND_BY_SURFACE, VERIFY_MY_ORDER } from '#app/utils/brand.ts'
import { resolveHostSurface } from '#app/utils/host-surface.server.ts'
import { type Route } from './+types/index.ts'

export async function action({ request }: Route.ActionArgs) {
	return loginAction({ request })
}

/**
 * The ONLY genuinely host-aware brand in the app. `/` is the one route every
 * surface may serve (isPathAllowedOnSurface), so this single file titles three
 * hosts; the body already branches below. Every other route knows its own
 * audience at build time and imports its brand directly (app/utils/brand.ts).
 */
export const meta = ({ data }: Route.MetaArgs) => {
	const brand = BRAND_BY_SURFACE[data?.surface ?? 'internal']
	return [
		{ title: `${brand.name} — Dimple Color` },
		{ name: 'description', content: brand.tagline },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	// Which branded surface is this host? Drives the neutral landing variant
	// below. Unset env vars → 'internal' → the unchanged staff-login landing.
	const surface = resolveHostSurface(request)
	return { isLoggedIn: Boolean(userId), surface }
}

/**
 * A neutral, login-free landing for the customer and prescriber hosts. Neither
 * audience ever browses to `/` intentionally — they arrive via emailed secure
 * links — so the bare host must NOT expose the staff login. Tone mirrors the
 * prescriber invalid-link page (app/routes/prescriber/_layout.tsx). Content is
 * the friendly-copy half of host gating (issue 47).
 */
function NeutralLanding({
	surface,
}: {
	surface: 'customer' | 'prescriber'
}) {
	const copy =
		surface === 'customer'
			? {
					eyebrow: 'Dimple Color',
					heading: 'Verify your prescription',
					body: 'No login needed — follow the secure link in the verification email we sent with your order.',
				}
			: {
					eyebrow: 'Dimple Color',
					heading: 'Prescriber verification',
					body: 'Use the decision link in the verification request we emailed you. It works for the life of the order.',
				}
	return (
		<main className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 px-6 py-24 text-center">
			<p className="text-primary text-sm font-semibold tracking-widest uppercase">
				{copy.eyebrow}
			</p>
			<h1 className="text-3xl font-bold sm:text-4xl">{copy.heading}</h1>
			<p className="text-muted-foreground max-w-xl text-lg">{copy.body}</p>
		</main>
	)
}

/**
 * Staff portal landing. There is no marketing funnel here — customers and
 * prescribers only ever arrive via emailed secure links — so the homepage's
 * real audience is staff: the login IS the hero. Layout vocabulary borrowed
 * from the propte landing pages (eyebrow label, big heading, bordered cards).
 *
 * On a branded customer/prescriber host we render the login-free NeutralLanding
 * instead (host gating, issue 47); staff and internal hosts see this unchanged.
 */
export default function Index({ loaderData }: Route.ComponentProps) {
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	if (loaderData.surface === 'customer' || loaderData.surface === 'prescriber') {
		return <NeutralLanding surface={loaderData.surface} />
	}
	return (
		<main className="mx-auto w-full max-w-5xl px-6 pt-16 pb-24 sm:pt-24">
			<div className="grid items-stretch gap-12 lg:grid-cols-[1fr_minmax(0,26rem)]">
				{/* Brand + orientation */}
				<section>
					<p className="text-primary text-sm font-semibold tracking-widest uppercase">
						Dimple Color
					</p>
					{/* Static: this branch is the STAFF portal hero (customer and
					    prescriber hosts return NeutralLanding above), so its audience
					    is known here — no host resolution needed. */}
					<h1 className="mt-4 text-4xl leading-tight font-bold sm:text-5xl">
						{VERIFY_MY_ORDER.name}
					</h1>
					<p className="text-muted-foreground mt-4 max-w-xl text-lg">
						Prescription verification for Dimple Color contact-lens orders —
						the staff portal for reviews, timers, and order status.
					</p>

					<div className="mt-10 grid gap-4 sm:grid-cols-2">
						<div className="border-border rounded-lg border p-6">
							<h2 className="font-semibold">Verifying your prescription?</h2>
							<p className="text-muted-foreground mt-2 text-sm">
								No login needed — follow the secure link in the verification
								email we sent with your order.
							</p>
						</div>
						<div className="border-border rounded-lg border p-6">
							<h2 className="font-semibold">Prescriber?</h2>
							<p className="text-muted-foreground mt-2 text-sm">
								Use the decision link in the verification request we emailed
								you. It works for the life of the order.
							</p>
						</div>
					</div>
				</section>

				{/* Login hero */}
				<section className="border-border bg-card flex flex-col justify-center rounded-xl border p-8 shadow-sm">
					{loaderData.isLoggedIn ? (
						<div className="flex flex-col gap-4">
							<div>
								<h2 className="text-xl font-semibold">You're logged in</h2>
								<p className="text-muted-foreground mt-1 text-sm">
									Pick up where you left off.
								</p>
							</div>
							<Link
								to="/dashboard"
								className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 py-3 text-center font-semibold"
							>
								Open dashboard
							</Link>
							<Link
								to="/verifications"
								className="text-center text-sm font-medium hover:underline"
							>
								Verifications queue <Icon name="arrow-right" />
							</Link>
						</div>
					) : (
						<>
							<div className="mb-6">
								<h2 className="text-xl font-semibold">Staff login</h2>
								<p className="text-muted-foreground mt-1 text-sm">
									Enter your email and we'll send you a login link.
								</p>
							</div>
							<LoginForm redirectTo={redirectTo} />
						</>
					)}
				</section>
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
